/**
 * @file 常用的校验函数
 */
/**
 * 校验字符串是否为有效的国内手机号
 *
 * @category Validator
 * @param phone - 输入的字符串
 * @returns 是否为有效的国内手机号
 */
export function isValidPhoneNumber(phone) {
    return /^1[3456789]\d{9}$/.test(phone);
}
/**
 * 校验字符串是否为有效的邮箱地址
 *
 * @category Validator
 * @param email - 输入的字符串
 * @returns 是否为有效的邮箱地址
 */
export function isValidEmail(email) {
    return /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/.test(email);
}
/**
 * 校验字符串是否为合法的 JSON String (能通过 JSON.parse)
 *
 * @category Validator
 * @param str - 输入的字符串
 * @returns 是否为合法的 JSON String
 */
export function isValidJSONString(str) {
    try {
        JSON.parse(str);
    }
    catch (e) {
        return false;
    }
    return true;
}
/**
 * 校验字符串是否为合法的带协议前缀的完整 URL. 支持 URL 包含中文.
 *
 * @category Validator
 * @param str - 输入的字符串
 * @returns 是否为合法 URL
 */
export function isValidURL(str) {
    const exp = /^(http|https):\/\/[\w\-_\u4E00-\u9FA5:/]+(\.[\w\-_\u4E00-\u9FA5]+)+([\u4E00-\u9FA5\w\-.,@?^=%&:/~+#]*[\u4E00-\u9FA5\w\-@?^=%&/~+#])?$/;
    return exp.test(str);
}
/**
 * 校验字符串是否为合法的大陆身份证
 *
 * @category Validator
 * @param id - 字符串
 * @returns 是否
 */
export function isValidMainlandId(id) {
    const idcardExp = /^[1-9][0-9]{5}([1][9][0-9]{2}|[2][0][0|1][0-9])([0][1-9]|[1][0|1|2])([0][1-9]|[1|2][0-9]|[3][0|1])[0-9]{3}([0-9]|[X])$/;
    // 判断格式是否正确
    const format = idcardExp.test(id);
    if (!format) {
        return false;
    }
    // 加权因子
    const weightFactor = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    // 校验码
    const checkCode = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];
    const last = id[17];
    const seventeen = id.substring(0, 17);
    /*
     * ISO 7064:1983.MOD 11-2
     * 判断最后一位校验码是否正确
     */
    const arr = seventeen.split('');
    const len = arr.length;
    let num = 0;
    for (let i = 0; i < len; i += 1) {
        num += Number(arr[i]) * weightFactor[i];
    }
    // 获取余数
    const resisue = num % 11;
    const lastNo = checkCode[resisue];
    // 返回验证结果，校验码和格式同时正确才算是合法的身份证号码
    return last === lastNo;
}
/**
 * 校验字符串是否为合法的香港身份证
 *
 * @category Validator
 * @param id - 字符串
 * @returns 是否
 */
export function isValidHongKongId(id) {
    const exp = /[A-Z]{1,2}[0-9]{6}\([0-9A]\)/;
    return exp.test(id);
}
/**
 * 校验字符串是否为合法的澳门身份证
 *
 * @category Validator
 * @param id - 字符串
 * @returns 是否
 */
export function isValidMacauId(id) {
    const exp = /[157][0-9]{6}\([0-9A-Z]\)/;
    return exp.test(id);
}
/**
 * 校验字符串是否为合法的台湾身份证
 *
 * @category Validator
 * @param id - 字符串
 * @returns 是否
 */
export function isValidTaiWanId(id) {
    if (id.length !== 10) {
        return false;
    }
    const eng = id.substring(0, 1);
    const num = id.substring(1, 10);
    const sex = Number(id.substring(1, 2));
    const regNum = /^[0-9]+.?[0-9]*/;
    const regEng = /^[A-Za-z]+$/;
    if (!regNum.test(num)) {
        return false;
    }
    if (sex !== 1 && sex !== 2) {
        return false;
    }
    if (!regEng.test(eng)) {
        return false;
    }
    const key = 'ABCDEFGHJKLMNPQRSTUVXYWZIO';
    const value = [
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
        '16',
        '17',
        '18',
        '19',
        '20',
        '21',
        '22',
        '23',
        '24',
        '25',
        '26',
        '27',
        '28',
        '29',
        '30',
        '31',
        '32',
        '33',
        '34',
        '35',
    ];
    const pos = key.indexOf(eng);
    if (pos === -1) {
        return false;
    }
    const x1 = value[pos].substring(0, 1);
    const x2 = value[pos].substring(1, 2);
    const y = Number(x1) +
        9 * Number(x2) +
        8 * Number(num.substring(0, 1)) +
        7 * Number(num.substring(1, 2)) +
        6 * Number(num.substring(2, 3)) +
        5 * Number(num.substring(3, 4)) +
        4 * Number(num.substring(4, 5)) +
        3 * Number(num.substring(5, 6)) +
        2 * Number(num.substring(6, 7)) +
        Number(num.substring(7, 8));
    const checkResult = y % 10;
    if (10 - checkResult === Number(num.substring(8, 9))) {
        return true;
    }
    if (checkResult === 10 && num.substring(8, 9) === '0') {
        // 若取余之后的结果为 0，那么末尾的数为 0
        return test;
    }
    return false;
}
/**
 * 校验字符串是否为合法的护照 ID
 *
 * @category Validator
 * @param id - 字符串
 * @returns 是否
 */
export function isValidPassportId(id) {
    // 规则： 14/15 开头 + 7 位数字, G + 8 位数字, P + 7 位数字, S/D + 7 或 8 位数字,等
    const exp = /^([a-zA-z]|[0-9]){5,17}$/;
    return exp.test(id);
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidmFsaWRhdG9ycy5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uLy4uL3NyYy9mdW5jdGlvbnMvdmFsaWRhdG9ycy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQTs7R0FFRztBQUVIOzs7Ozs7R0FNRztBQUNILE1BQU0sVUFBVSxrQkFBa0IsQ0FBQyxLQUFhO0lBQzlDLE9BQU8sbUJBQW1CLENBQUMsSUFBSSxDQUFDLEtBQUssQ0FBQyxDQUFBO0FBQ3hDLENBQUM7QUFFRDs7Ozs7O0dBTUc7QUFDSCxNQUFNLFVBQVUsWUFBWSxDQUFDLEtBQWE7SUFDeEMsT0FBTywrQ0FBK0MsQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUE7QUFDcEUsQ0FBQztBQUVEOzs7Ozs7R0FNRztBQUNILE1BQU0sVUFBVSxpQkFBaUIsQ0FBQyxHQUFXO0lBQzNDLElBQUk7UUFDRixJQUFJLENBQUMsS0FBSyxDQUFDLEdBQUcsQ0FBQyxDQUFBO0tBQ2hCO0lBQUMsT0FBTyxDQUFDLEVBQUU7UUFDVixPQUFPLEtBQUssQ0FBQTtLQUNiO0lBRUQsT0FBTyxJQUFJLENBQUE7QUFDYixDQUFDO0FBRUQ7Ozs7OztHQU1HO0FBQ0gsTUFBTSxVQUFVLFVBQVUsQ0FBQyxHQUFXO0lBQ3BDLE1BQU0sR0FBRyxHQUNQLHVJQUF1SSxDQUFBO0lBRXpJLE9BQU8sR0FBRyxDQUFDLElBQUksQ0FBQyxHQUFHLENBQUMsQ0FBQTtBQUN0QixDQUFDO0FBRUQ7Ozs7OztHQU1HO0FBQ0gsTUFBTSxVQUFVLGlCQUFpQixDQUFDLEVBQVU7SUFDMUMsTUFBTSxTQUFTLEdBQ2Isd0hBQXdILENBQUE7SUFFMUgsV0FBVztJQUNYLE1BQU0sTUFBTSxHQUFHLFNBQVMsQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDLENBQUE7SUFFakMsSUFBSSxDQUFDLE1BQU0sRUFBRTtRQUNYLE9BQU8sS0FBSyxDQUFBO0tBQ2I7SUFFRCxPQUFPO0lBQ1AsTUFBTSxZQUFZLEdBQUcsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxFQUFFLEVBQUUsRUFBRSxDQUFDLEVBQUUsQ0FBQyxFQUFFLENBQUMsRUFBRSxDQUFDLEVBQUUsQ0FBQyxFQUFFLENBQUMsRUFBRSxDQUFDLEVBQUUsQ0FBQyxFQUFFLENBQUMsRUFBRSxFQUFFLEVBQUUsQ0FBQyxFQUFFLENBQUMsRUFBRSxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUE7SUFDMUUsTUFBTTtJQUNOLE1BQU0sU0FBUyxHQUFHLENBQUMsR0FBRyxFQUFFLEdBQUcsRUFBRSxHQUFHLEVBQUUsR0FBRyxFQUFFLEdBQUcsRUFBRSxHQUFHLEVBQUUsR0FBRyxFQUFFLEdBQUcsRUFBRSxHQUFHLEVBQUUsR0FBRyxFQUFFLEdBQUcsQ0FBQyxDQUFBO0lBQ3pFLE1BQU0sSUFBSSxHQUFHLEVBQUUsQ0FBQyxFQUFFLENBQUMsQ0FBQTtJQUNuQixNQUFNLFNBQVMsR0FBRyxFQUFFLENBQUMsU0FBUyxDQUFDLENBQUMsRUFBRSxFQUFFLENBQUMsQ0FBQTtJQUVyQzs7O09BR0c7SUFDSCxNQUFNLEdBQUcsR0FBRyxTQUFTLENBQUMsS0FBSyxDQUFDLEVBQUUsQ0FBQyxDQUFBO0lBQy9CLE1BQU0sR0FBRyxHQUFHLEdBQUcsQ0FBQyxNQUFNLENBQUE7SUFDdEIsSUFBSSxHQUFHLEdBQUcsQ0FBQyxDQUFBO0lBQ1gsS0FBSyxJQUFJLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQyxHQUFHLEdBQUcsRUFBRSxDQUFDLElBQUksQ0FBQyxFQUFFO1FBQy9CLEdBQUcsSUFBSSxNQUFNLENBQUMsR0FBRyxDQUFDLENBQUMsQ0FBQyxDQUFDLEdBQUcsWUFBWSxDQUFDLENBQUMsQ0FBQyxDQUFBO0tBQ3hDO0lBRUQsT0FBTztJQUNQLE1BQU0sT0FBTyxHQUFHLEdBQUcsR0FBRyxFQUFFLENBQUE7SUFFeEIsTUFBTSxNQUFNLEdBQUcsU0FBUyxDQUFDLE9BQU8sQ0FBQyxDQUFBO0lBRWpDLCtCQUErQjtJQUMvQixPQUFPLElBQUksS0FBSyxNQUFNLENBQUE7QUFDeEIsQ0FBQztBQUVEOzs7Ozs7R0FNRztBQUNILE1BQU0sVUFBVSxpQkFBaUIsQ0FBQyxFQUFVO0lBQzFDLE1BQU0sR0FBRyxHQUFHLDhCQUE4QixDQUFBO0lBQzFDLE9BQU8sR0FBRyxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUMsQ0FBQTtBQUNyQixDQUFDO0FBRUQ7Ozs7OztHQU1HO0FBQ0gsTUFBTSxVQUFVLGNBQWMsQ0FBQyxFQUFVO0lBQ3ZDLE1BQU0sR0FBRyxHQUFHLDJCQUEyQixDQUFBO0lBQ3ZDLE9BQU8sR0FBRyxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUMsQ0FBQTtBQUNyQixDQUFDO0FBRUQ7Ozs7OztHQU1HO0FBQ0gsTUFBTSxVQUFVLGVBQWUsQ0FBQyxFQUFVO0lBQ3hDLElBQUksRUFBRSxDQUFDLE1BQU0sS0FBSyxFQUFFLEVBQUU7UUFDcEIsT0FBTyxLQUFLLENBQUE7S0FDYjtJQUVELE1BQU0sR0FBRyxHQUFHLEVBQUUsQ0FBQyxTQUFTLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQyxDQUFBO0lBQzlCLE1BQU0sR0FBRyxHQUFHLEVBQUUsQ0FBQyxTQUFTLENBQUMsQ0FBQyxFQUFFLEVBQUUsQ0FBQyxDQUFBO0lBQy9CLE1BQU0sR0FBRyxHQUFHLE1BQU0sQ0FBQyxFQUFFLENBQUMsU0FBUyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQyxDQUFBO0lBRXRDLE1BQU0sTUFBTSxHQUFHLGlCQUFpQixDQUFBO0lBQ2hDLE1BQU0sTUFBTSxHQUFHLGFBQWEsQ0FBQTtJQUU1QixJQUFJLENBQUMsTUFBTSxDQUFDLElBQUksQ0FBQyxHQUFHLENBQUMsRUFBRTtRQUNyQixPQUFPLEtBQUssQ0FBQTtLQUNiO0lBRUQsSUFBSSxHQUFHLEtBQUssQ0FBQyxJQUFJLEdBQUcsS0FBSyxDQUFDLEVBQUU7UUFDMUIsT0FBTyxLQUFLLENBQUE7S0FDYjtJQUVELElBQUksQ0FBQyxNQUFNLENBQUMsSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFO1FBQ3JCLE9BQU8sS0FBSyxDQUFBO0tBQ2I7SUFFRCxNQUFNLEdBQUcsR0FBRyw0QkFBNEIsQ0FBQTtJQUN4QyxNQUFNLEtBQUssR0FBRztRQUNaLElBQUk7UUFDSixJQUFJO1FBQ0osSUFBSTtRQUNKLElBQUk7UUFDSixJQUFJO1FBQ0osSUFBSTtRQUNKLElBQUk7UUFDSixJQUFJO1FBQ0osSUFBSTtRQUNKLElBQUk7UUFDSixJQUFJO1FBQ0osSUFBSTtRQUNKLElBQUk7UUFDSixJQUFJO1FBQ0osSUFBSTtRQUNKLElBQUk7UUFDSixJQUFJO1FBQ0osSUFBSTtRQUNKLElBQUk7UUFDSixJQUFJO1FBQ0osSUFBSTtRQUNKLElBQUk7UUFDSixJQUFJO1FBQ0osSUFBSTtRQUNKLElBQUk7UUFDSixJQUFJO0tBQ0wsQ0FBQTtJQUVELE1BQU0sR0FBRyxHQUFHLEdBQUcsQ0FBQyxPQUFPLENBQUMsR0FBRyxDQUFDLENBQUE7SUFDNUIsSUFBSSxHQUFHLEtBQUssQ0FBQyxDQUFDLEVBQUU7UUFDZCxPQUFPLEtBQUssQ0FBQTtLQUNiO0lBRUQsTUFBTSxFQUFFLEdBQUcsS0FBSyxDQUFDLEdBQUcsQ0FBQyxDQUFDLFNBQVMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUE7SUFDckMsTUFBTSxFQUFFLEdBQUcsS0FBSyxDQUFDLEdBQUcsQ0FBQyxDQUFDLFNBQVMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUE7SUFDckMsTUFBTSxDQUFDLEdBQ0wsTUFBTSxDQUFDLEVBQUUsQ0FBQztRQUNWLENBQUMsR0FBRyxNQUFNLENBQUMsRUFBRSxDQUFDO1FBQ2QsQ0FBQyxHQUFHLE1BQU0sQ0FBQyxHQUFHLENBQUMsU0FBUyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQztRQUMvQixDQUFDLEdBQUcsTUFBTSxDQUFDLEdBQUcsQ0FBQyxTQUFTLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQyxDQUFDO1FBQy9CLENBQUMsR0FBRyxNQUFNLENBQUMsR0FBRyxDQUFDLFNBQVMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUM7UUFDL0IsQ0FBQyxHQUFHLE1BQU0sQ0FBQyxHQUFHLENBQUMsU0FBUyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQztRQUMvQixDQUFDLEdBQUcsTUFBTSxDQUFDLEdBQUcsQ0FBQyxTQUFTLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQyxDQUFDO1FBQy9CLENBQUMsR0FBRyxNQUFNLENBQUMsR0FBRyxDQUFDLFNBQVMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUM7UUFDL0IsQ0FBQyxHQUFHLE1BQU0sQ0FBQyxHQUFHLENBQUMsU0FBUyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQztRQUMvQixNQUFNLENBQUMsR0FBRyxDQUFDLFNBQVMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUMsQ0FBQTtJQUU3QixNQUFNLFdBQVcsR0FBRyxDQUFDLEdBQUcsRUFBRSxDQUFBO0lBQzFCLElBQUksRUFBRSxHQUFHLFdBQVcsS0FBSyxNQUFNLENBQUMsR0FBRyxDQUFDLFNBQVMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUMsRUFBRTtRQUNwRCxPQUFPLElBQUksQ0FBQTtLQUNaO0lBRUQsSUFBSSxXQUFXLEtBQUssRUFBRSxJQUFJLEdBQUcsQ0FBQyxTQUFTLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQyxLQUFLLEdBQUcsRUFBRTtRQUNyRCx3QkFBd0I7UUFDeEIsT0FBTyxJQUFJLENBQUE7S0FDWjtJQUVELE9BQU8sS0FBSyxDQUFBO0FBQ2QsQ0FBQztBQUVEOzs7Ozs7R0FNRztBQUNILE1BQU0sVUFBVSxpQkFBaUIsQ0FBQyxFQUFVO0lBQzFDLGdFQUFnRTtJQUNoRSxNQUFNLEdBQUcsR0FBRywwQkFBMEIsQ0FBQTtJQUV0QyxPQUFPLEdBQUcsQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDLENBQUE7QUFDckIsQ0FBQyJ9